import _ from 'lodash'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { HandledDeliveryFee, HandleDeliveryFeeSize, PackageSize, StoreDeliveryFeeType } from 'types/storeDeliveryFee'
import { SelectOption } from 'types/common'

const initialState: HandledDeliveryFee = {
  region_type: 'any',
  region_name: 'その他地域一律',
  sizes: [
    {
      package_size: '60',
      delivery_fee_amount: 0,
    },
  ],
}

type UseHandleDeliveryFee = {
  deliveryFees: HandledDeliveryFee[]
  addRegion: () => void
  addSize: (regionIndex: number) => void
  feeType: 'bulk' | 'individual'
  handleDeliveryFeeAmount: (regionIndex: number, sizeIndex: number, event: ChangeEvent<HTMLInputElement>) => void
  handleFeeType: (event: ChangeEvent<HTMLInputElement>) => void
  removeRegion: (regionIndex: number) => void
  removeSize: (regionIndex: number, sizeIndex: number) => void
  selectRegion: (regionIndex: number, regionName: string) => void
  selectSize: (regionIndex: number, sizeIndex: number, selectedSize: PackageSize) => void
}

const useHandleDeliveryFee = (
  storeDeliveryFees: StoreDeliveryFeeType[],
  PACKAGE_SIZES: PackageSize[],
  regionNameOptions: SelectOption[]
): UseHandleDeliveryFee => {
  const [deliveryFees, setDeliveryFees] = useState<HandledDeliveryFee[]>([initialState])
  const [feeType, setFeeType] = useState<'bulk' | 'individual'>('bulk')

  const addRegion = useCallback(() => {
    setDeliveryFees((prevState) => {
      const region_name = regionNameOptions.filter((DELIVERY_REGION_NAME) => {
        return prevState.every((deliveryFee) => deliveryFee.region_name !== DELIVERY_REGION_NAME.value)
      })[0]?.value
      if (!region_name) return prevState
      const region_type = region_name === 'その他地域一律' ? 'any' : 'prefecture'
      const nextRegion: HandledDeliveryFee = {
        region_type,
        region_name,
        sizes: [
          {
            package_size: '60',
            delivery_fee_amount: 0,
          },
        ],
      }
      const nextState = [...prevState, nextRegion]
      return [...nextState]
    })
  }, [setDeliveryFees, regionNameOptions])

  const addSize = useCallback(
    (regionIndex: number) => {
      setDeliveryFees((prevState) => {
        const nextState = prevState
        const deliveryFee = prevState[regionIndex]
        const nextPackageSize = PACKAGE_SIZES.filter((PACKAGE_SIZE) =>
          deliveryFee.sizes.every((size) => size.package_size !== PACKAGE_SIZE)
        )[0]
        // 追加できるサイズがない
        if (!nextPackageSize) return prevState
        const nextSize = { delivery_fee_amount: 0, package_size: nextPackageSize }
        nextState[regionIndex].sizes = [...deliveryFee.sizes, nextSize]
        return [...nextState]
      })
    },
    [PACKAGE_SIZES, setDeliveryFees]
  )

  const removeRegion = useCallback(
    (regionIndex: number) => {
      setDeliveryFees((prevState) => {
        const nextState = prevState.filter((_, i) => i !== regionIndex)
        return [...nextState]
      })
    },
    [setDeliveryFees]
  )

  const selectRegion = useCallback(
    (regionIndex: number, regionName: string) => {
      setDeliveryFees((prevState) => {
        const nextState = prevState
        // TODO areaが追加されたら対応する
        nextState[regionIndex].region_type = regionName === 'その他地域一律' ? 'any' : 'prefecture'
        nextState[regionIndex].region_name = regionName
        return [...nextState]
      })
    },
    [setDeliveryFees]
  )

  const removeSize = useCallback(
    (regionIndex: number, sizeIndex: number) => {
      setDeliveryFees((prevState) => {
        const nextState = prevState
        nextState[regionIndex].sizes = [...prevState[regionIndex].sizes.filter((_, i) => i !== sizeIndex)]
        return [...nextState]
      })
    },
    [setDeliveryFees]
  )

  const selectSize = useCallback(
    (regionIndex: number, sizeIndex: number, selectedSize: PackageSize) => {
      setDeliveryFees((prevState) => {
        const nextState = prevState
        nextState[regionIndex].sizes[sizeIndex].package_size = selectedSize
        return [...nextState]
      })
    },
    [setDeliveryFees]
  )

  const handleDeliveryFeeAmount = useCallback(
    (regionIndex: number, sizeIndex: number, event: ChangeEvent<HTMLInputElement>) => {
      setDeliveryFees((prevState) => {
        const nextState = prevState
        let value = event.target.value
        // 空文字であれば0にする
        if (/(?:)/.test(value)) {
          nextState[regionIndex].sizes[sizeIndex].delivery_fee_amount = 0
        }
        // 全角数字が存在していれば全て半角数字に変換する
        value = value.replace(/[０-９]/g, (s) => {
          return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
        })
        // 数値であれば数値型に変換して更新する
        if (/^[0-9]+$/.test(value)) {
          nextState[regionIndex].sizes[sizeIndex].delivery_fee_amount = Number(value)
        }
        return [...nextState]
      })
    },
    [setDeliveryFees]
  )

  const handleFeeType = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      if (value !== 'bulk' && value !== 'individual') return
      setFeeType(value)
      // 一括設定に変更したらregionを「その他地域一律」のみにする
      if (value === 'individual') return
      setDeliveryFees((prevState) => {
        const nextState = prevState.filter((deliveryFee) => deliveryFee.region_type === 'any')
        return [...nextState]
      })
    },
    [setFeeType, setDeliveryFees]
  )

  useEffect(() => {
    if (storeDeliveryFees.length === 0) return
    // region単位で分け直す
    const regionNames = storeDeliveryFees.reduce((prev: string[], storeDeliveryFee) => {
      const addedRegionNames = storeDeliveryFee.regions.reduce((prevRegionNames: string[], region) => {
        return [...prevRegionNames, region.region_name]
      }, [])
      return [...prev, ...addedRegionNames]
    }, [])
    const uniqRegionNames = _.uniq(regionNames)
    // 設定されている地域が「その他地域一律」のみであれば一括設定として扱う
    setFeeType(uniqRegionNames.length === 1 && uniqRegionNames[0] === 'その他地域一律' ? 'bulk' : 'individual')
    const _deliveryFees: HandledDeliveryFee[] = uniqRegionNames.map((region_name) => {
      const sizes = storeDeliveryFees
        .reduce((prev: HandleDeliveryFeeSize[], storeDeliveryFee) => {
          const region = storeDeliveryFee.regions.find((region) => region.region_name === region_name)
          if (!region) return prev
          const size: HandleDeliveryFeeSize = {
            delivery_fee_amount: region.delivery_fee_amount,
            package_size: storeDeliveryFee.package_size,
          }
          return [...prev, size]
        }, [])
        .sort((a, b) => (Number(a.package_size) < Number(b.package_size) ? -1 : 1))
      const region_type = region_name === 'その他地域一律' ? 'any' : 'prefecture'
      return {
        region_type,
        region_name,
        sizes,
      }
    })
    setDeliveryFees([..._deliveryFees])
  }, [storeDeliveryFees])

  return {
    addRegion,
    addSize,
    deliveryFees,
    feeType,
    handleDeliveryFeeAmount,
    handleFeeType,
    removeRegion,
    removeSize,
    selectRegion,
    selectSize,
  }
}

export default useHandleDeliveryFee
